import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Button, Collapse, Divider, Grid, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import objectHash from "object-hash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton.tsx";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill.tsx";
import type { DimensionDescriptor, DimensionsStructure } from "../../../../../../../../../api/types/svTypes.ts";
import GlobalDndProvider from "../../../../../../../../common/GlobalDndProvider.tsx";
import type { FieldWithOrder, FieldWithSorting } from "../../../../../hooks/FieldWithOrder";
import ColumnsContainer from "./ColumnsContainer.tsx";
import type { OptionField } from "./ColumnsOptions.types.ts";
import SortingContainer from "./sorting/SortingContainer.tsx";

const isSelectionValid = (selection: FieldWithOrder[]) => {
  return !selection.some((fwo) => !fwo.field.caption);
};

enum TabNames {
  columns = "columns",
  sorting = "sorting",
}

interface Props {
  open: boolean;
  selectedFields: FieldWithOrder[];
  sortedFields: FieldWithSorting[];
  defaultSelectedFields?: FieldWithOrder[];
  defaultSortedFields?: FieldWithSorting[];
  showCustomSetupAppliedInfo?: boolean;
  dimensions: DimensionDescriptor[];
  groupStructure: DimensionsStructure;
  withAllocation: boolean;
  onApplySelection: (selection: FieldWithOrder[]) => void;
  onApplySorting: (sorting: FieldWithSorting[]) => void;
  onApplySetup?: (selection: FieldWithOrder[], sorting: FieldWithSorting[]) => void;
  onClose: () => void;
}

export default function ColumnsOptions(props: Props) {
  const {
    dimensions,
    selectedFields,
    sortedFields,
    defaultSelectedFields = [],
    defaultSortedFields = [],
    showCustomSetupAppliedInfo = false,
    open,
    groupStructure,
    withAllocation,
    onApplySelection,
    onApplySorting,
    onApplySetup,
    onClose,
  } = props;

  const [selectedTab, setSelectedTab] = useState(TabNames.columns);
  const [dirtyColumnsSelection, setDirtyColumnsSelection] = useState<FieldWithOrder[]>(() =>
    structuredClone(selectedFields)
  );
  const [dirtyColumnsSorting, setDirtyColumnsSorting] = useState<FieldWithSorting[]>(() =>
    structuredClone(sortedFields)
  );

  const validDimensions = React.useMemo(
    () => (withAllocation ? dimensions : dimensions.filter((d) => d.glAllowed === true)),
    [withAllocation, dimensions]
  );

  const updateDirtyColumnsSelection = useCallback((selection: FieldWithOrder[]) => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setDirtyColumnsSelection(structuredClone(selection));
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setDirtyColumnsSorting((prev) =>
      structuredClone(prev.filter((sf) => selection.some((f) => f.field.name === sf.field.name)))
    );
  }, []);

  useEffect(() => {
    // TODO #209
    updateDirtyColumnsSelection(selectedFields);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [selectedFields]);

  // TODO #209
  // eslint-disable-next-line @eslint-react/set-state-in-effect
  useEffect(() => setDirtyColumnsSorting(structuredClone(sortedFields)), [sortedFields]);

  const columnsHasChanged = useMemo(() => {
    const originalFieldsHash = objectHash(selectedFields);
    const dirtySelectionHash = objectHash(dirtyColumnsSelection);
    if (originalFieldsHash === dirtySelectionHash) {
      return false;
    }
    return isSelectionValid(dirtyColumnsSelection);
  }, [selectedFields, dirtyColumnsSelection]);

  const sortingHasChanged = useMemo(() => {
    const originalFieldsHash = objectHash(sortedFields);
    const dirtySelectionHash = objectHash(dirtyColumnsSorting);
    if (originalFieldsHash === dirtySelectionHash) {
      return false;
    }
    return isSelectionValid(dirtyColumnsSorting);
  }, [sortedFields, dirtyColumnsSorting]);

  const options = useMemo(() => {
    const getGroupName = (field: DimensionDescriptor) => {
      const group = groupStructure.groups.find((g) => g.dimensions.indexOf(field.name) > -1);
      return group?.caption || "General";
    };
    return validDimensions
      .map(
        (f) =>
          ({
            id: f.name,
            label: f.caption,
            field: f,
            group: getGroupName(f),
          }) as OptionField
      )
      .sort((a, b) => {
        if (a.group > b.group) return 1;
        if (a.group < b.group) return -1;
        return 0;
      });
  }, [validDimensions, groupStructure]);

  const availableSortingOptions = useMemo(() => {
    return options.filter((op) => dirtyColumnsSelection.some((sf) => sf.field.name === op.id));
  }, [options, dirtyColumnsSelection]);

  const applySelection = useCallback(() => {
    const hasChanges = columnsHasChanged || sortingHasChanged;
    if (hasChanges) {
      dirtyColumnsSelection.forEach((field, index) => {
        field.order = index;
      });
      dirtyColumnsSorting.forEach((field, index) => {
        field.order = index;
      });

      if (onApplySetup) {
        onApplySetup(structuredClone(dirtyColumnsSelection), structuredClone(dirtyColumnsSorting));
      } else {
        if (columnsHasChanged) {
          onApplySelection(structuredClone(dirtyColumnsSelection));
        }
        if (sortingHasChanged) {
          onApplySorting(structuredClone(dirtyColumnsSorting));
        }
      }
    }
    onClose();
  }, [
    dirtyColumnsSelection,
    dirtyColumnsSorting,
    columnsHasChanged,
    sortingHasChanged,
    onApplySelection,
    onApplySorting,
    onApplySetup,
    onClose,
  ]);

  const closeDialog = useCallback(() => {
    updateDirtyColumnsSelection(structuredClone(selectedFields));
    onClose();
  }, [selectedFields, onClose, updateDirtyColumnsSelection]);

  const resetToDefaultSelection = useCallback(() => {
    updateDirtyColumnsSelection(structuredClone(defaultSelectedFields));
    setDirtyColumnsSorting(structuredClone(defaultSortedFields));
    setSelectedTab(TabNames.columns);
  }, [defaultSelectedFields, defaultSortedFields, updateDirtyColumnsSelection]);

  return (
    <Collapse
      in={open}
      orientation="horizontal"
      sx={{ position: "absolute", top: 0, right: 0, height: "100%", borderRadius: 0, zIndex: 500 }}
    >
      <Paper sx={{ display: "flex", flex: 1, width: "348px", height: "100%", borderRadius: 0 }} elevation={3}>
        <Stack sx={{ width: "100%" }}>
          <Grid container sx={{ px: 3, pt: 2, alignItems: "center", width: "100%" }}>
            <Typography variant="h6">Column options</Typography>
            <HorizontalFill />
            <CloseIconButton onClick={closeDialog} />
          </Grid>
          {showCustomSetupAppliedInfo && (
            <>
              <Divider />
              <Grid container sx={{ px: 2, pt: 1, pb: 1, alignItems: "center", width: "100%", gap: 0.5 }}>
                <CheckCircleRoundedIcon sx={{ color: "success.main" }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Custom selection applied
                </Typography>
                <HorizontalFill />
                <Button variant="outlined" size="small" onClick={resetToDefaultSelection}>
                  Reset to default
                </Button>
              </Grid>
              <Divider />
            </>
          )}
          <Grid container sx={{ width: "100%" }}>
            <Tabs
              sx={{ display: "flex", flex: 1 }}
              variant="fullWidth"
              value={selectedTab}
              onChange={(_, value: TabNames) => setSelectedTab(value)}
            >
              <Tab label="Columns" value={TabNames.columns} />
              <Tab label="Sorting" value={TabNames.sorting} />
            </Tabs>
          </Grid>
          <Divider />
          <GlobalDndProvider>
            <Grid container sx={{ flex: 1, overflowY: "auto", alignItems: "start", width: "100%" }}>
              {selectedTab === TabNames.columns && (
                <ColumnsContainer
                  selectedFields={dirtyColumnsSelection}
                  options={options}
                  onSelectionChanged={updateDirtyColumnsSelection}
                />
              )}
              {selectedTab === TabNames.sorting && (
                <SortingContainer
                  selectedFields={dirtyColumnsSorting}
                  options={availableSortingOptions}
                  onSelectionChanged={setDirtyColumnsSorting}
                />
              )}
            </Grid>
          </GlobalDndProvider>

          <Divider />
          <Grid container sx={{ gap: 1, px: 2, py: 2, width: "100%" }}>
            <HorizontalFill />
            <Button variant="text" color="secondary" onClick={closeDialog}>
              Cancel
            </Button>
            <Button variant="contained" disabled={!columnsHasChanged && !sortingHasChanged} onClick={applySelection}>
              Apply
            </Button>
          </Grid>
        </Stack>
      </Paper>
    </Collapse>
  );
}
